import React, { useEffect, useState, useMemo } from "react";
import { format } from "date-fns";
import { useAnnouncementCards, useDispatch, useSelections } from "hooks";
import { PageLoader, PermissionsRequired } from "library";
import MarketGroup from "./MarketGroup";
import { TitleIcon } from "components/TitleIcon";
import { toggleSelection } from "../Betslip/betslipSlice";
import type { ToggleSelectionPayload } from "hooks/firestore/betting/types";
import useDisableNativeScrollRestoration from "hooks/useDisableNativeScrollRestoration";
import { Helmet } from "react-helmet";
import { useLocation } from "@gatsbyjs/reach-router";
import { useEvent } from "hooks/firestore/v2/betting/useEvent";
import { useOutrightMarkets } from "hooks/useOutrightMarkets";
import { marketSearch } from "utilities/sharedBettingUtilities";
import { AnnouncementCards } from "components/AnnouncementCards";
import { EventBlocked } from "sections/Betting/components/EventBlocked";
import { MarketsFilter } from "./MarketsFilter";
import { usePreferences } from "hooks/firestore/v2/user/usePreferences";
import { ReactComponent as EmptyIcon } from "sections/Share/assets/empty.svg";
import { NextBettingButtons } from "components/layout/NextBettingButtons";
import { PageMessage } from "components/PageMessage";
import { Page } from "components/Page";
import { parseDate } from "utilities/dateUtilities";

import * as styles from "./MatchPage.module.scss";

type OutrightPageProps = {
  id?: string;
  titles: any;
  path?: string;
};

const OutrightPage = ({ id, titles }: OutrightPageProps) => {
  const { event: outright, isLoading: loading } = useEvent(id, "all");
  const [expandedMarkets, setExpandedMarkets] = useState<string[]>([]);
  const [searchFilter, setSearchFilter] = useState("");
  const cards = useAnnouncementCards();

  useDisableNativeScrollRestoration();
  const { preferences: userPreferences } = usePreferences();

  const markets = useOutrightMarkets(outright);

  const {
    props: { selections },
  } = useSelections();

  const filteredMarkets = useMemo(
    () => marketSearch({ markets, searchFilter }),
    [markets, searchFilter],
  );

  useEffect(() => {
    if (loading || !outright) {
      return;
    }

    if (searchFilter && filteredMarkets.length > 0) {
      const marketsToExpand = filteredMarkets.map((market) => market.name);
      setExpandedMarkets(marketsToExpand);
    } else {
      const activeMarket = outright.markets?.find(
        (market) => market.status === "ACTIVE",
      );
      if (activeMarket) {
        setExpandedMarkets([activeMarket.name]);
      }
    }
  }, [loading, outright, searchFilter, filteredMarkets]);

  const toggleMarket = (marketName) => {
    if (expandedMarkets.includes(marketName)) {
      setExpandedMarkets(
        expandedMarkets.filter(
          (existingMarket) => existingMarket !== marketName,
        ),
      );
    } else {
      setExpandedMarkets([...expandedMarkets, marketName]);
    }
  };

  const dispatch = useDispatch();
  const location = useLocation();

  const toggleSelectionWrapper = (pick: ToggleSelectionPayload) =>
    dispatch(toggleSelection(outright, pick, outright.hub));

  if (loading) {
    return <PageLoader />;
  }

  if (!outright) {
    return (
      <PageMessage
        image={<EmptyIcon />}
        title="Outright not found"
        subTitle="This page is no longer available"
      >
        <NextBettingButtons />
      </PageMessage>
    );
  }

  const title = !loading ? titles[outright?.sport?.toLowerCase()] : null;

  const permissionsRequired = ["viewBettingMarkets"];
  if (outright.hub === "esports") {
    permissionsRequired.push("viewEsportsMarkets");
  } else {
    permissionsRequired.push("viewSportsMarkets");
  }

  return (
    <EventBlocked filters={outright.filters} loading={loading}>
      <PermissionsRequired permissionsRequired={permissionsRequired}>
        <Page
          variant="full"
          showSeparator={false}
          title={
            <div className={styles.outrightHeader}>
              <div className={styles.outrightTitleIcon}>
                <TitleIcon title={title} svg />
              </div>
              <h1>{outright.eventName}</h1>
            </div>
          }
          subTitle={format(
            outright?.markets?.[0]?.nextBetStop || outright?.scheduledStartTime,
            "EEEE, MMMM do HH:mm",
          )}
        >
          <Helmet>
            <link
              rel="canonical"
              href={`${location?.origin}${location?.pathname}`}
            />
          </Helmet>
          <div className={styles.outright}>
            {markets.length > 0 && (
              <MarketsFilter
                className={styles.filter}
                value={searchFilter}
                onValueChange={setSearchFilter}
                onValueReset={() => setSearchFilter("")}
                hasResults={filteredMarkets.length > 0}
              >
                <>
                  <AnnouncementCards cards={cards} noSpacing />
                  {filteredMarkets.map((market) => (
                    <MarketGroup
                      key={`outright-market-${market.name}-${market.id}`}
                      name={market.name}
                      markets={[market]}
                      event={outright}
                      toggleMarkets={() => toggleMarket(market.name)}
                      isOpen={expandedMarkets.includes(market.name)}
                      oddsFormat={userPreferences?.oddsFormat.toLowerCase()}
                      toggleSelection={toggleSelectionWrapper}
                      selections={selections}
                      promotionIsAvailable={false}
                      isOutright
                    />
                  ))}
                </>
              </MarketsFilter>
            )}
          </div>
        </Page>
      </PermissionsRequired>
    </EventBlocked>
  );
};

export default OutrightPage;
